/**
 * Fase 2 «Conduta e Contexto» — tipos de fio da conduta configurável.
 *
 * Espelham CAMPO-A-CAMPO (R15) o que o Engine serializa:
 *  - os 5 enums: `prisma/schema.prisma` (`ConductRegisterPolicy` … `ConductEscalationPosture`)
 *    e a migration `20260805213000_conduct_profile_columns`;
 *  - a resolução: `apps/engine/src/modules/ai-orchestration/ai-conductor/conduct-profile-origin.ts`
 *    (`ConductProfileResolution`), servida por `GET /v1/conduct-profile/effective`.
 *
 * Re-declarados como union de literais porque `@lume/shared` não pode importar
 * `@prisma/client` (precedente `admin/config-tenant/types.ts:41`, `NativeSchedulingMode`).
 * ⚠️ Valor novo num enum do schema exige atualizar aqui — é o custo declarado do espelho.
 * **Não inventar rótulo nem opção que o enum não tem.**
 */

export type ConductRegisterPolicy = 'hold_persona' | 'mirror_within_persona' | 'mirror_client';
export type ConductGuidanceStyle = 'conversational' | 'menus_allowed' | 'menus_preferred';
export type ConductResponseLength = 'short' | 'standard' | 'detailed';
export type ConductConfirmationStyle = 'echo' | 'implicit';
export type ConductEscalationPosture = 'conservative' | 'balanced' | 'autonomous';

/** Os 5 eixos, na ordem canônica de `CONDUCT_PROFILE_FIELDS` do Engine. */
export const CONDUCT_AXES = [
  'registerPolicy',
  'guidanceStyle',
  'responseLength',
  'confirmationStyle',
  'escalationPosture',
] as const;

export type ConductAxis = (typeof CONDUCT_AXES)[number];

/** Os 4 níveis da cascata, do mais específico ao menos. */
export type ConductOriginLevel = 'persona' | 'playbook' | 'tenant' | 'platform';

/** Os 3 níveis que são linha de banco (o `platform` não é editável por tenant algum). */
export type ConductEditableLevel = 'persona' | 'playbook' | 'tenant';

/** Por que um nível NÃO participa desta resolução. Espelha `ConductLevelAbsentReason`. */
export type ConductLevelAbsentReason =
  | 'no_playbook_selected'
  | 'playbook_not_active'
  | 'playbook_has_no_persona'
  | 'persona_not_active'
  | 'persona_level_gated_off';

export interface ConductLevelPresence {
  readonly present: boolean;
  readonly id: string | null;
  readonly name: string | null;
  readonly absentReason: ConductLevelAbsentReason | null;
}

export interface ConductAxisResolution {
  readonly field: ConductAxis;
  readonly value: string;
  readonly origin: ConductOriginLevel;
  readonly opinions: {
    readonly persona: string | null;
    readonly playbook: string | null;
    readonly tenant: string | null;
    readonly platform: string;
  };
}

export interface ConductProfileResolution {
  readonly axes: readonly ConductAxisResolution[];
  readonly levels: {
    readonly persona: ConductLevelPresence;
    readonly playbook: ConductLevelPresence;
    readonly tenant: ConductLevelPresence;
  };
}

/**
 * As 5 colunas como qualquer um dos 3 níveis as expõe na leitura E aceita na escrita.
 * `null` = «este nível não opinou» — e é ENVIÁVEL nos três PATCHes (verificado ao vivo:
 * persona/playbook/tenant devolvem 200 com `null` e voltam a herdar). É o que torna
 * «voltar a herdar» alcançável, ao contrário do `personaId` (que o DTO tipa `string?` sem
 * `| null`, e por isso tem a opção «— nenhuma —» desabilitada no seletor).
 */
export interface ConductColumns {
  conductRegisterPolicy?: ConductRegisterPolicy | null;
  conductGuidanceStyle?: ConductGuidanceStyle | null;
  conductResponseLength?: ConductResponseLength | null;
  conductConfirmationStyle?: ConductConfirmationStyle | null;
  conductEscalationPosture?: ConductEscalationPosture | null;
}

/** O nome da coluna `conduct*` correspondente a cada eixo. */
export const CONDUCT_AXIS_COLUMN: Readonly<Record<ConductAxis, keyof ConductColumns>> = {
  registerPolicy: 'conductRegisterPolicy',
  guidanceStyle: 'conductGuidanceStyle',
  responseLength: 'conductResponseLength',
  confirmationStyle: 'conductConfirmationStyle',
  escalationPosture: 'conductEscalationPosture',
};
